import localStorage from 'local-storage';

/**
 * Consent state for the store's Google tags and Meta Pixel.
 *
 * head-template.xml sets Google Consent Mode v2 defaults before gtag config
 * runs — denied for `region: ['EEA','GB','CH']`, granted everywhere else — and
 * revokes the Pixel for those same visitors. That means an AU visitor's tags
 * behave exactly as they always have, and only a European visitor is gated.
 *
 * Consent Mode defaults reset on every page load, so a stored choice has to be
 * replayed early on each load: `restoreConsent()` runs from src/index.js before
 * React renders.
 *
 * Google evaluates `region` itself, server-side, so it is always right about
 * where the visitor is. The Pixel has no equivalent, and neither does our
 * banner, so both fall back to the visitor's IANA timezone — deliberately
 * erring toward asking (every `Europe/*` zone, not just EEA members), because
 * showing a banner to someone who didn't need one is harmless and the reverse
 * is not.
 */

const STORAGE_KEY = 'no_analytics_consent';

export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';

const GRANTED_SIGNALS = {
  ad_storage: CONSENT_GRANTED,
  ad_user_data: CONSENT_GRANTED,
  ad_personalization: CONSENT_GRANTED,
  analytics_storage: CONSENT_GRANTED,
};

const DENIED_SIGNALS = {
  ad_storage: CONSENT_DENIED,
  ad_user_data: CONSENT_DENIED,
  ad_personalization: CONSENT_DENIED,
  analytics_storage: CONSENT_DENIED,
};

/** Whether this visitor is somewhere the tags default to denied. */
export function isConsentRequired() {
  if (typeof window === 'undefined') return false;
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return timeZone.startsWith('Europe/');
  } catch (error) {
    // No Intl support is rare enough, and old enough, that it isn't worth
    // gating analytics on; the Google-side regional default still applies.
    return false;
  }
}

/** `'granted'`, `'denied'`, or `null` when the visitor hasn't chosen yet. */
export function getStoredConsent() {
  const stored = localStorage.get(STORAGE_KEY);
  return stored === CONSENT_GRANTED || stored === CONSENT_DENIED
    ? stored
    : null;
}

export function shouldPromptForConsent() {
  return isConsentRequired() && getStoredConsent() === null;
}

function pushConsentSignals(state) {
  const signals = state === CONSENT_GRANTED ? GRANTED_SIGNALS : DENIED_SIGNALS;

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', signals);
  }

  if (typeof window.fbq === 'function') {
    window.fbq('consent', state === CONSENT_GRANTED ? 'grant' : 'revoke');
  }

  // Lets GTM tags key off consent without reading storage themselves.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'consent_update', consent_state: state });
}

/** Records the visitor's choice and pushes it to every tag library. */
export function setConsent(state) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.set(STORAGE_KEY, state);
    pushConsentSignals(state);
  } catch (error) {
    // Never let a consent write break the page.
  }
}

/**
 * Replays a previously stored choice. Called once from src/index.js, before
 * render, so tags see the visitor's decision ahead of any event.
 */
export function restoreConsent() {
  if (typeof window === 'undefined') return;
  const stored = getStoredConsent();
  if (stored === null) return;
  try {
    pushConsentSignals(stored);
  } catch (error) {
    // As above — analytics must never break the page.
  }
}
